import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { IconButton, Tooltip, Typography } from '@mui/material'
import { Add, CenterFocusStrong, Remove } from '@mui/icons-material'
import { standardTooltipProps } from '@/common/components'
import { zoomControlIconButtonSx, zoomPercentageTextSx } from './ZoomControls.styles'

const MIN_ZOOM = 0.5
const MAX_ZOOM = 2
const ZOOM_STEP = 0.1
const DEFAULT_ZOOM = 1
const SHOW_INDICATOR_DURATION = 2000

interface ZoomControlsProps {
  readonly zoomLevel: number
  readonly onZoomIn: () => void
  readonly onZoomOut: () => void
  readonly onZoomReset: () => void
  readonly showZoomLevel?: boolean
}

export const ZoomControls: FC<ZoomControlsProps> = ({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  showZoomLevel = false,
}) => {
  const zoomPercentage = Math.round(zoomLevel * 100)

  return (
    <>
      <Typography variant="caption" sx={zoomPercentageTextSx(showZoomLevel)}>
        {zoomPercentage}%
      </Typography>

      <Tooltip {...standardTooltipProps} title="Zoom out">
        <IconButton onClick={onZoomOut} size="small" sx={zoomControlIconButtonSx}>
          <Remove fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip {...standardTooltipProps} title="Reset zoom">
        <IconButton onClick={onZoomReset} size="small" sx={zoomControlIconButtonSx}>
          <CenterFocusStrong fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip {...standardTooltipProps} title="Zoom in">
        <IconButton onClick={onZoomIn} size="small" sx={zoomControlIconButtonSx}>
          <Add fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  )
}

interface UseZoomLevelReturn {
  readonly zoomLevel: number
  readonly fontSize: string
  readonly handleZoomIn: () => void
  readonly handleZoomOut: () => void
  readonly handleZoomReset: () => void
  readonly showZoomLevel: boolean
}

// eslint-disable-next-line react-refresh/only-export-components
export function useZoomLevel(isOpen: boolean): UseZoomLevelReturn {
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM)
  const [showZoomLevel, setShowZoomLevel] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setZoomLevel(DEFAULT_ZOOM)
      setShowZoomLevel(false)
    }
  }, [isOpen])

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM))
    setShowZoomLevel(true)
    setTimeout(() => setShowZoomLevel(false), SHOW_INDICATOR_DURATION)
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM))
    setShowZoomLevel(true)
    setTimeout(() => setShowZoomLevel(false), SHOW_INDICATOR_DURATION)
  }, [])

  const handleZoomReset = useCallback(() => {
    setZoomLevel(DEFAULT_ZOOM)
    setShowZoomLevel(true)
    setTimeout(() => setShowZoomLevel(false), SHOW_INDICATOR_DURATION)
  }, [])

  const fontSize = `${zoomLevel}rem`

  return {
    zoomLevel,
    fontSize,
    handleZoomIn,
    handleZoomOut,
    handleZoomReset,
    showZoomLevel,
  }
}
